// Device-local sync bookkeeping: which records changed since the last push
// (the outbox) and where the pull cursor stands (the meta). Always IndexedDB,
// even in shell mode (Tauri webview) — this is per-device state, not part of
// the synced dataset. Ported from assetto-gallery's src/sync/outbox.ts, see
// _planning/referencia-assetto.md §6.3.

import {
    getRecord,
    putRecord,
    putRecords,
    deleteRecordsIf,
    getAllEntries,
    SYNC_OUTBOX_STORE,
    SYNC_META_STORE,
} from './db'

const META_KEY = 'meta'

function hasIndexedDB() {
    return typeof window !== 'undefined' && typeof indexedDB !== 'undefined'
}

// Storage modules call markDirty() on every user write; the sync engine
// subscribes here to debounce a push. Plain callbacks (no framework signal)
// so this module has zero React/UI dependency.
const listeners = new Set()

export function onOutboxChange(listener) {
    listeners.add(listener)
    return () => listeners.delete(listener)
}

// While the engine applies records coming FROM the server, local writes must
// not re-enter the outbox (they'd echo back forever). config-strike's only
// local-write path today (storageService -> markDirty) never goes through
// this flag itself; applyIncoming (collect.js) wraps its writes in
// withRemoteApply defensively, so any future write path that also calls
// markDirty stays safe too.
let applyingRemote = false

export async function withRemoteApply(fn) {
    applyingRemote = true
    try {
        await fn()
    } finally {
        applyingRemote = false
    }
}

function outboxKey(kind, recordId) {
    // `kind` never contains "|", so this is collision-free even if recordId does.
    return `${kind}|${recordId}`
}

/**
 * Records that a local record changed and must be pushed. Never throws — a
 * sync bookkeeping failure must not break the actual save/delete that
 * triggered it (best-effort contract, see storageService.js).
 */
export async function markDirty(kind, recordId, opts = {}) {
    if (applyingRemote) return
    if (!hasIndexedDB()) return // SSR / no IndexedDB support
    try {
        const entry = {
            kind,
            recordId,
            deleted: opts.deleted ?? false,
            updatedAt: opts.updatedAt ?? new Date().toISOString(),
        }
        await putRecord(SYNC_OUTBOX_STORE, outboxKey(kind, recordId), entry)
        for (const listener of listeners) listener()
    } catch {
        // Best-effort: the record is still saved locally; a later full sync
        // (or any subsequent write) re-covers it.
    }
}

/** Bulk counterpart to markDirty, same best-effort/never-throws contract. */
export async function markManyDirty(changes) {
    if (applyingRemote) return
    if (!hasIndexedDB()) return
    if (!changes || changes.length === 0) return
    try {
        const fallback = new Date().toISOString()
        const entries = changes.map((c) => [
            outboxKey(c.kind, c.recordId),
            { kind: c.kind, recordId: c.recordId, deleted: c.deleted ?? false, updatedAt: c.updatedAt ?? fallback },
        ])
        await putRecords(SYNC_OUTBOX_STORE, entries)
        for (const listener of listeners) listener()
    } catch {
        // Best-effort, same as markDirty.
    }
}

export async function listOutbox() {
    if (!hasIndexedDB()) return []
    const entries = await getAllEntries(SYNC_OUTBOX_STORE)
    return entries.map(([, value]) => value)
}

/**
 * Removes entries after a successful push — but only if the record wasn't
 * dirtied AGAIN while the push was in flight (same key, newer updatedAt).
 * One atomic transaction for the whole batch.
 */
export async function clearOutboxEntries(pushed) {
    if (!hasIndexedDB() || !pushed || pushed.length === 0) return
    const expected = new Map(pushed.map((e) => [outboxKey(e.kind, e.recordId), e]))
    await deleteRecordsIf(SYNC_OUTBOX_STORE, [...expected.keys()], (current, key) => {
        const entry = expected.get(key)
        return current.updatedAt === entry.updatedAt && current.deleted === entry.deleted
    })
}

export async function getOutboxEntry(kind, recordId) {
    if (!hasIndexedDB()) return null
    return getRecord(SYNC_OUTBOX_STORE, outboxKey(kind, recordId))
}

export async function getSyncMeta() {
    if (!hasIndexedDB()) return { cursor: 0 }
    const meta = await getRecord(SYNC_META_STORE, META_KEY)
    return meta ?? { cursor: 0 }
}

export async function setSyncMeta(patch) {
    const next = { ...(await getSyncMeta()), ...patch }
    if (hasIndexedDB()) await putRecord(SYNC_META_STORE, META_KEY, next)
    return next
}
